import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarIcon, ChevronDownIcon } from 'lucide-react';
import type { DatePreset } from '../../types/dashboard';
import { useFilters } from '../../contexts/FilterContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { DATE_PRESET_LABELS, MONTH_COUNT, makeRange, monthLabel, resolveRange } from '../../utils/calendar';
const PRESETS: DatePreset[] = ['last13', 'ytd', 'lastYear', 'custom'];
export function DateRangeDropdown({
  disabled,
  disabledReason
}: {
  disabled?: boolean;
  disabledReason?: string;
}) {
  const {
    filters,
    update
  } = useFilters();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(ref, close, open);
  const range = resolveRange(filters);
  const months = Array.from({
    length: MONTH_COUNT
  }, (_, i) => i);
  return <div ref={ref} className="relative" title={disabled ? disabledReason : undefined} data-id="element-221" data-mp="wz8bbg4l656r">
      <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} className={`flex h-8 items-center gap-1.5 rounded-md border pl-2.5 pr-1.5 text-[12.5px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/30 ${disabled ? 'cursor-not-allowed border-dashed border-line bg-canvas opacity-60' : 'border-navy/40 bg-navy-50 hover:border-navy/60'}`} data-id="element-222" data-mp="x11l34ahx5iz">
        <CalendarIcon className="h-3.5 w-3.5 text-navy" data-id="element-223" data-mp="15zdibp5vb1ke" />
        <span className="whitespace-nowrap font-semibold text-ink" data-id="element-224" data-mp="161zphnoujf23">{disabled ? 'Date range not applied' : DATE_PRESET_LABELS[filters.datePreset]}</span>
        {!disabled && <span className="whitespace-nowrap text-ink-muted" data-id="element-225" data-mp="3qqx8cos1j13z">{range.label}</span>}
        <ChevronDownIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-226" data-mp="3lqrnbrga0cuv" />
      </button>
      <AnimatePresence data-id="element-227" data-mp="2drvecg518qrz">
        {open && <motion.div role="dialog" aria-label="Date range" initial={{
        opacity: 0,
        scale: 0.96,
        y: -4
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        transformOrigin: 'top left'
      }} className="absolute left-0 z-40 mt-1.5 w-[300px] rounded-lg border border-line bg-white p-2 shadow-lg" data-id="element-228" data-mp="2ds3qu6x8tu2n">
            <ul className="space-y-0.5" data-id="element-229" data-mp="1yvw7f8tnswof">
              {PRESETS.map(p => {
            const r = p === 'custom' ? makeRange(Math.min(filters.customStart, filters.customEnd), Math.max(filters.customStart, filters.customEnd)) : resolveRange({
              ...filters,
              datePreset: p
            });
            const active = filters.datePreset === p;
            return <li key={p} data-id="element-230" data-mp="1g4d3mzrt9elc">
                    <button type="button" onClick={() => {
                update({
                  datePreset: p
                });
                if (p !== 'custom') close();
              }} className={`flex w-full items-center justify-between rounded px-2.5 py-2 text-left text-[12.5px] ${active ? 'bg-navy-50 font-semibold text-navy' : 'text-ink hover:bg-canvas'}`} data-id="element-231" data-mp="1g2im7vfr04m0">
                      <span className="flex items-center gap-2" data-id="element-232" data-mp="1fgnwafka5yfv">
                        <span className={`h-3 w-3 rounded-full border ${active ? 'border-[4px] border-navy' : 'border-line-strong'}`} data-id="element-233" data-mp="1feta6xx9w0a7" />
                        {DATE_PRESET_LABELS[p]}
                      </span>
                      <span className="text-xs font-normal text-ink-muted" data-id="element-234" data-mp="1p8jwpx9ua0n9">{r.label}</span>
                    </button>
                  </li>;
          })}
            </ul>
            <p className="mt-1 border-t border-line px-2.5 pt-2 text-[11px] text-ink-subtle" data-id="element-235" data-mp="1z2snogj81ljb">
              Latest completed month: {monthLabel(MONTH_COUNT - 1)}. Oct ’26 is month-to-date (snapshot 10/16/26) and is excluded so MoM never compares a partial month.
            </p>
            {filters.datePreset === 'custom' && <div className="mt-2 grid grid-cols-2 gap-2 border-t border-line px-1 pt-3" data-id="element-236" data-mp="19838po0nde5">
                {(['customStart', 'customEnd'] as const).map(key => <label key={key} className="text-[11px] font-medium text-ink-muted" data-id="element-237" data-mp="6a42gpxfabd8">
                    {key === 'customStart' ? 'From month' : 'To month'}
                    <select value={filters[key]} onChange={e => {
              const v = Number(e.target.value);
              update(key === 'customStart' ? {
                customStart: v
              } : {
                customEnd: v
              });
            }} className="mt-1 h-8 w-full rounded border border-line bg-white px-2 text-xs text-ink focus:border-navy/50 focus:outline-none" data-id="element-238" data-mp="39pt0j97aci1f">
                      {months.map(m => <option key={m} value={m} data-id="element-239" data-mp="2jdfvxq5wxyfn">
                          {monthLabel(m)}
                        </option>)}
                    </select>
                  </label>)}
                <p className="col-span-2 text-[11px] text-ink-subtle" data-id="element-240" data-mp="29n3ew7xb4ne9">Sample data covers Jan 2024 – Sep 2026. YoY needs 12 prior months.</p>
              </div>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}